// A person's own weeks (round 44): this week, anything owed, and the history.
//
// The first thing on the page is the thing to do: this week's sheet, or last
// week's if it has not gone in. Starting a week is one tap and lands on it.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api';
import { Banner } from '../../ui';
import { StatusBadge } from '../ui/StatusBadge';
import { CardList, RowCard } from '../ui/RowCard';
import { LinkedRow } from '../ui/LinkedRow';
import { SortHeader } from '../ui/SortHeader';
import { sortRows, useSort } from '../../lib/sortRows';
import { minutesToHm } from '../../lib/timesheetHours';
import { weekLabel } from '../../lib/timesheetWeeks';
import { timesheets, type MineSummary, type MineWeek } from '../../timesheetsApi';

type SortKey = 'week' | 'status' | 'worked' | 'leave';

export function MyWeeks({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  const [mine, setMine] = useState<MineSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { sortKey, sortDir, onSort } = useSort<SortKey>('week', 'desc');

  useEffect(() => {
    timesheets
      .mine()
      .then(setMine)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Could not load your timesheets.'));
  }, []);

  const visible = useMemo(() => {
    const rows = mine?.weeks ?? [];
    return sortRows(
      rows,
      (w: MineWeek) =>
        sortKey === 'week'
          ? w.weekStart
          : sortKey === 'status'
            ? w.status
            : sortKey === 'worked'
              ? w.totals.workedMinutes
              : w.totals.leaveMinutes + w.totals.fullLeaveDays * 1440 || null,
      sortDir
    );
  }, [mine, sortKey, sortDir]);

  async function start(weekStart: string) {
    setBusy(true);
    setError(null);
    try {
      const detail = await timesheets.createWeek(weekStart);
      navigate(`/app/timesheets/${detail.sheet.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start that week.');
      setBusy(false);
    }
  }

  if (error && !mine) return <Banner kind="error">{error}</Banner>;
  if (!mine) return <p className="muted">Loading…</p>;
  if (!mine.onTimesheets && mine.weeks.length === 0) {
    return compact ? null : (
      <section className="panel">
        <p className="!mb-0">You are not set up to fill in timesheets. Ask an admin if you should be.</p>
      </section>
    );
  }

  const byWeek = new Map(mine.weeks.map((w) => [w.weekStart, w]));
  const thisWeek = byWeek.get(mine.thisWeekStart) ?? null;
  const lastWeek = byWeek.get(mine.lastWeekStart) ?? null;
  // With no sheet, last week is owed only if the server counts it missing,
  // which it never does for a week that ended before this person joined
  // (owesWeek's rule, src/timesheets/people.ts).
  const lastOwed =
    mine.onTimesheets &&
    (lastWeek
      ? lastWeek.status === 'draft' || lastWeek.status === 'returned'
      : mine.missing.includes(mine.lastWeekStart));
  const olderMissing = mine.missing.filter((w) => w !== mine.lastWeekStart);
  // A week the office sent back is the worker's to fix whichever week it is;
  // last week's has its own panel above.
  const sentBack = mine.weeks.filter((w) => w.status === 'returned' && w.weekStart !== mine.lastWeekStart);

  return (
    <>
      {error && <Banner kind="error">{error}</Banner>}

      {lastOwed && (
        <section className="panel attention">
          <h2 className="!mb-1">Last week, {weekLabel(mine.lastWeekStart)}</h2>
          <p className="!mb-3 text-sm text-gray-700">
            {!lastWeek
              ? 'Not started yet. Fill it in and sign it so the office can do the pays.'
              : lastWeek.status === 'returned'
                ? `Sent back to you: “${lastWeek.returnReason}”`
                : 'Started but not submitted yet.'}
          </p>
          {lastWeek ? (
            <Link className="btn-primary" to={`/app/timesheets/${lastWeek.id}`}>
              {lastWeek.status === 'returned' ? 'Fix last week' : 'Finish last week'}
            </Link>
          ) : (
            <button type="button" className="btn-primary" disabled={busy} onClick={() => void start(mine.lastWeekStart)}>
              Start last week
            </button>
          )}
        </section>
      )}

      {sentBack.map((w) => (
        <section key={w.id} className="panel attention">
          <h2 className="!mb-1">Sent back: {w.weekLabel}</h2>
          <p className="!mb-3 text-sm text-gray-700">“{w.returnReason}”</p>
          <Link className="btn-primary" to={`/app/timesheets/${w.id}`}>
            Fix this week
          </Link>
        </section>
      ))}

      {mine.onTimesheets && (
        <section className="panel">
          <div className="row mb-2">
            <h2 className="!mb-0">This week, {weekLabel(mine.thisWeekStart)}</h2>
          </div>
          {thisWeek ? (
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge kind="timesheet" status={thisWeek.status} />
              <span className="text-sm tabular-nums text-gray-700">
                {minutesToHm(thisWeek.totals.workedMinutes)} worked so far
              </span>
              <Link
                className={lastOwed ? 'btn-secondary' : 'btn-primary'}
                to={`/app/timesheets/${thisWeek.id}`}
              >
                Open this week
              </Link>
            </div>
          ) : (
            <button
              type="button"
              className={lastOwed ? 'btn-secondary' : 'btn-primary'}
              disabled={busy}
              onClick={() => void start(mine.thisWeekStart)}
            >
              Start this week
            </button>
          )}
        </section>
      )}

      {olderMissing.length > 0 && !compact && (
        <section className="panel">
          <h2>Weeks not started</h2>
          <ul className="space-y-2">
            {olderMissing.map((w) => (
              <li key={w} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm">{weekLabel(w)}</span>
                <button type="button" className="btn-secondary btn-sm" disabled={busy} onClick={() => void start(w)}>
                  Start it
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!compact && mine.weeks.length > 0 && (
        <section>
          <h2>{compact ? 'My weeks' : 'Earlier weeks'}</h2>
          <CardList>
            {visible.map((w) => (
              <RowCard
                key={w.id}
                to={`/app/timesheets/${w.id}`}
                title={`Week of ${w.weekLabel}`}
                badges={<StatusBadge kind="timesheet" status={w.status} />}
              >
                {minutesToHm(w.totals.workedMinutes)} worked
                {w.totals.fullLeaveDays > 0 && <> · {w.totals.fullLeaveDays} day{w.totals.fullLeaveDays === 1 ? '' : 's'} leave</>}
              </RowCard>
            ))}
          </CardList>
          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Week" sortKey="week" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Worked" sortKey="worked" current={sortKey} dir={sortDir} onSort={onSort} className="text-right" />
                  <SortHeader label="Leave" sortKey="leave" current={sortKey} dir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {visible.map((w) => (
                  <LinkedRow key={w.id} to={`/app/timesheets/${w.id}`}>
                    <td>
                      <Link className="table-link" to={`/app/timesheets/${w.id}`}>
                        {w.weekLabel}
                      </Link>
                    </td>
                    <td>
                      <StatusBadge kind="timesheet" status={w.status} />
                    </td>
                    <td className="text-right tabular-nums">{minutesToHm(w.totals.workedMinutes)}</td>
                    <td className="table-muted">
                      {[
                        w.totals.leaveMinutes > 0 ? minutesToHm(w.totals.leaveMinutes) : null,
                        w.totals.fullLeaveDays > 0 ? `${w.totals.fullLeaveDays} full day${w.totals.fullLeaveDays === 1 ? '' : 's'}` : null,
                      ]
                        .filter(Boolean)
                        .join(' + ')}
                    </td>
                  </LinkedRow>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
